import { midi } from './synth.js';

/**
 * A very small chip sequencer. Two looping tracks, scheduled ahead of time on
 * the audio clock so they stay tight even when the render loop stutters.
 */

const LOOKAHEAD = 0.12;
const TICK_MS = 25;

const R = null; // rest

const TRACKS = {
  title: {
    bpm: 116,
    stepsPerBeat: 4,
    lead: [
      69, R, 76, R, 74, R, 72, R, 69, R, 72, R, 74, R, R, R,
      67, R, 74, R, 72, R, 71, R, 67, R, 71, R, 74, R, R, R,
      69, R, 76, R, 81, R, 79, R, 76, R, 74, R, 72, R, R, R,
      65, R, 72, R, 76, R, 79, R, 76, R, 72, R, 69, R, R, R,
    ],
    bass: [
      33, R, R, R, 33, R, 40, R, 33, R, R, R, 40, R, R, R,
      31, R, R, R, 31, R, 38, R, 31, R, R, R, 38, R, R, R,
      29, R, R, R, 29, R, 36, R, 29, R, R, R, 36, R, R, R,
      28, R, R, R, 28, R, 35, R, 28, R, R, R, 35, R, R, R,
    ],
    hats: [
      1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0,
      1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0,
      1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0,
      1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 1, 1,
    ],
    leadGain: 0.24,
    bassGain: 0.3,
  },

  boss: {
    bpm: 150,
    stepsPerBeat: 4,
    lead: [
      74, R, 74, 77, R, 74, R, 81, 80, R, 77, R, 74, R, R, R,
      74, R, 74, 77, R, 74, R, 82, 81, R, 77, R, 74, R, R, R,
      75, R, 75, 79, R, 75, R, 82, 81, R, 79, R, 75, R, R, R,
      74, 75, 77, 79, 81, 82, 81, 79, 77, 75, 74, R, 69, R, R, R,
    ],
    bass: [
      38, 38, R, 38, 38, R, 38, R, 38, 38, R, 38, 45, R, 44, R,
      38, 38, R, 38, 38, R, 38, R, 38, 38, R, 38, 45, R, 44, R,
      39, 39, R, 39, 39, R, 39, R, 39, 39, R, 39, 46, R, 45, R,
      38, 38, R, 38, 41, R, 43, R, 44, R, 45, R, 46, R, 47, R,
    ],
    hats: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1,
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1,
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1,
      1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1,
    ],
    leadGain: 0.22,
    bassGain: 0.34,
  },
};

export function createMusic(synth) {
  let timer = 0;
  let track = null;
  let trackName = null;
  let step = 0;
  let nextTime = 0;

  function stepDuration(def) {
    return 60 / def.bpm / def.stepsPerBeat;
  }

  function scheduleStep(index, when) {
    const ctx = synth.context;
    if (!ctx) return;
    const delay = Math.max(0, when - ctx.currentTime);
    const bus = synth.musicBus;
    const dur = stepDuration(track);

    const leadNote = track.lead[index];
    if (leadNote != null) {
      synth.playToneOn(
        {
          type: 'square',
          freq: midi(leadNote),
          duration: dur * 1.7,
          gain: track.leadGain,
          attack: 0.006,
          delay,
        },
        bus,
      );
    }

    const bassNote = track.bass[index];
    if (bassNote != null) {
      synth.playToneOn(
        {
          type: 'triangle',
          freq: midi(bassNote),
          duration: dur * 1.9,
          gain: track.bassGain,
          attack: 0.004,
          delay,
        },
        bus,
      );
    }

    if (track.hats[index]) {
      synth.playToneOn(
        {
          type: 'square',
          freq: 5200,
          freqEnd: 3600,
          duration: dur * 0.4,
          gain: 0.05,
          attack: 0.001,
          delay,
        },
        bus,
      );
    }
  }

  function pump() {
    const ctx = synth.context;
    if (!ctx || !track) return;
    const dur = stepDuration(track);
    while (nextTime < ctx.currentTime + LOOKAHEAD) {
      scheduleStep(step, nextTime);
      nextTime += dur;
      step = (step + 1) % track.lead.length;
    }
  }

  return {
    get playing() {
      return trackName;
    },

    play(name) {
      if (trackName === name) return;
      this.stop();
      const def = TRACKS[name];
      if (!def) return;
      // Creating the context before a user gesture only earns a console
      // warning, so wait: main.js replays the track once audio is unlocked.
      if (!synth.state.unlocked) return;
      const ctx = synth.ensure();
      if (!ctx) return;

      track = def;
      trackName = name;
      step = 0;
      nextTime = ctx.currentTime + 0.08;
      pump();
      timer = window.setInterval(pump, TICK_MS);
    },

    stop() {
      if (timer) window.clearInterval(timer);
      timer = 0;
      track = null;
      trackName = null;
    },
  };
}
